import MultipleSelector, { Option } from "@/components/ui/multiselect";
import { cn } from "@/lib/utils";
import { Controller } from "react-hook-form";
import { toast } from "sonner";
import { ControlType, FieldKeys } from "./new-email-dialog";

type EmailOption = {
  label: string;
  value: string;
  leadName: string;
};

type EmailFieldProps = {
  name: FieldKeys;
  control: ControlType;
  label: string;
  leads: Option[] | undefined;
  onSearch: (value: string) => void;
  maxSelected?: number;
  actions?: React.ReactNode;
};

type SingleEmailFieldProps = {
  name: FieldKeys;
  control: ControlType;
  label: string;
  leads: Option[] | undefined;
  onSearch: (value: string) => void;
  actions?: React.ReactNode;
  maxSelectedMessage: string;
};

// Single select email field for "To" and "From"
const SingleEmailField = ({
  name,
  control,
  label,
  leads,
  onSearch,
  actions,
  maxSelectedMessage
}: SingleEmailFieldProps) => {
  return (
    <Controller
      name={name}
      control={control as any}
      render={({ field, fieldState }) => (
        <div
          className={cn(
            "flex border-b p-1 text-sm",
            fieldState.error && "bg-destructive/10",
          )}
        >
          <div className="text-muted-foreground">{label}:</div>
          <MultipleSelector
            onMaxSelected={() => {
              toast.error(
                maxSelectedMessage
              );
            }}
            maxSelected={1}
            className="w-full outline-0! ring-0! px-2 pt-0! border-none!"
            triggerSearchOnFocus
            value={(field.value as EmailOption[])?.map((email) => ({
              label: email.label,
              value: email.value,
              leadName: email.leadName,
            }))}
            onChange={(value) => {
              field.onChange(value);
            }}
            onSearch={async (value) => {
              onSearch(value);
              return leads || [];
            }}
            emptyIndicator={
              <p className="text-center text-sm">No contacts found</p>
            }
          />
          {actions}
        </div>
      )}
    />
  );
};

// Multi-select email field for "CC" and "BCC"
const EmailField = ({
  name,
  control,
  label,
  leads,
  onSearch,
  maxSelected = 100,
  actions,
}: EmailFieldProps) => {
  return (
    <Controller
      name={name}
      control={control as any}
      render={({ field, fieldState }) => (
        <div
          className={cn(
            "flex border-b p-1 text-sm",
            fieldState.error && "bg-destructive/10",
          )}
        >
          <div className="text-muted-foreground">{label}:</div>
          <MultipleSelector
            maxSelected={maxSelected}
            className="w-full outline-0! ring-0! px-2 pt-0! border-none!"
            triggerSearchOnFocus
            value={(field.value as EmailOption[])?.map((email) => ({
              label: email.label,
              value: email.value,
              leadName: email.leadName,
            }))}
            onChange={(value) => {
              field.onChange(value);
            }}
            onSearch={async (value) => {
              onSearch(value);
              return leads || [];
            }}
            emptyIndicator={
              <p className="text-center text-sm">No contacts found</p>
            }
          />
          {actions}
        </div>
      )}
    />
  );
};

export { EmailField, SingleEmailField };
export type { EmailFieldProps, SingleEmailFieldProps, EmailOption };
